import React, {CSSProperties, KeyboardEvent, useEffect, useState} from 'react'
import { DefaultTFuncReturn } from "i18next";
import ObjectUtil from "app/utils/objectUtil";
import { usePopper } from "react-popper";
import { Placement } from "@popperjs/core";
import {ArcRangeSliderCanChangeValueResult} from "./ArcRangeSlider";

export interface ArcInputProps {
  inputType?: "number" | "email" | "text" | "tel" | "password";
  id: string;
  name?: string;
  type?: InputTextType;
  value: string;// defined in Google Sheet
  label?: string;
  title?: string | DefaultTFuncReturn;
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
  readonly?: boolean;
  isRequired?: boolean;
  decimalLength?: number;
  validateMessage?: string | DefaultTFuncReturn;
  placeholder?: string | DefaultTFuncReturn;
  pattern?: string;
  prefix?: string; // optional
  suffix?: string;
  minFraction?: number;
  maxFraction?: number;
  maxLength?: number;
  step?: number;
  maxValue?: number;
  minValue?: number;
  onChange: ({ value, name, isValid }: ArcInputChangeResult) => void; // needed
  canChange?: (value: any) => ArcRangeSliderCanChangeValueResult;
  onKeyUp?: (e: any) => void;
  onKeyDown?: (e: any) => void;
  displayTitle?: boolean;
  popoverPlacement?: Placement;
  forceValidate?: boolean;
}

export interface ArcInputChangeResult {
  value: any;
  name: string;
  isValid: any;
}

interface Type {
  none: TypeDetail;
  w5p: TypeDetail;
  w10p: TypeDetail;
  w15p: TypeDetail;
  w20p: TypeDetail;
  w30p: TypeDetail;
  w35p: TypeDetail;
  w40p: TypeDetail;
  w50p: TypeDetail;
  w60p: TypeDetail;
  w70p: TypeDetail;
  w80p: TypeDetail;
  w90p: TypeDetail;
  w100p: TypeDetail;
  w50: TypeDetail;
  w80: TypeDetail;
  w100: TypeDetail;
  w150: TypeDetail;
  w200: TypeDetail;
  w250: TypeDetail;
  w300: TypeDetail;
  w400: TypeDetail;
  w450: TypeDetail;
  w500: TypeDetail;
  w800: TypeDetail;
  custom: TypeDetail
}

interface TypeDetail {
  className: string | null,
}

type InputTextType = keyof Type;

type InputClass = {
  [key in InputTextType]: TypeDetail;
}

export const TextAreaWidth = {
  none: 'InpSel',
  w5p: 'InpSel-w5',
  w10p: 'InpSel-w10',
  w15p: 'InpSel-w15',
  w20p: 'InpSel-w20',
  w30p: 'InpSel-w30',
  w35p: 'InpSel-w35',
  w40p: 'InpSel-w40',
  w50p: 'InpSel-w50',
  w60p: 'InpSel-w60',
  w70p: 'InpSel-w70',
  w80p: 'InpSel-w80',
  w90p: 'InpSel-w90',
  w100p: 'InpSel-w100',
  w50: 'InpSel-50',
  w80: 'InpSel-80',
  w100: 'InpSel-100',
  w150: 'InpSel-150',
  w200: 'InpSel-200',
  w250: 'InpSel-250',
  w300: 'InpSel-300',
  w400: 'InpSel-400',
  w450: 'InpSel-450',
  w500: 'InpSel-500',
  w800: 'InpSel-800',
} as const;

export const enumInputTextType: InputClass = {
  none: {
    className: TextAreaWidth.none,
  },
  w5p: {
    className: TextAreaWidth.w5p,
  },
  w10p: {
    className: TextAreaWidth.w10p,
  },
  w15p: {
    className: TextAreaWidth.w15p,
  },
  w20p: {
    className: TextAreaWidth.w20p,
  },
  w30p: {
    className: TextAreaWidth.w30p,
  },
  w35p: {
    className: TextAreaWidth.w35p,
  },
  w40p: {
    className: TextAreaWidth.w40p,
  },
  w50p: {
    className: TextAreaWidth.w50p,
  },
  w60p: {
    className: TextAreaWidth.w60p,
  },
  w70p: {
    className: TextAreaWidth.w70p,
  },
  w80p: {
    className: TextAreaWidth.w80p,
  },
  w90p: {
    className: TextAreaWidth.w90p,
  },
  w100p: {
    className: TextAreaWidth.w100p,
  },
  w50: {
    className: TextAreaWidth.w50,
  },
  w80: {
    className: TextAreaWidth.w80,
  },
  w100: {
    className: TextAreaWidth.w100,
  },
  w150: {
    className: TextAreaWidth.w150,
  },
  w200: {
    className: TextAreaWidth.w200,
  },
  w250: {
    className: TextAreaWidth.w250,
  },
  w300: {
    className: TextAreaWidth.w300,
  },
  w400: {
    className: TextAreaWidth.w400,
  },
  w450: {
    className: TextAreaWidth.w450,
  },
  w500: {
    className: TextAreaWidth.w500,
  },
  w800: {
    className: TextAreaWidth.w800,
  },
  custom: {
    className: ""
  }
} as const;

export default function ArcInput(props: Readonly<ArcInputProps>) {
  const {
    type = "w100p",
    className = "",
    disabled = false,
    readonly = false,
    placeholder = "내용을 입력하세요",
    id = "",
    name = "",
    displayTitle = props.displayTitle ?? true
  } = props;

  const [validateMsg, setValidateMsg] = React.useState<any>(props.validateMessage);
  const [valueText, setValueText] = React.useState<any>(undefined);
  const [isValid, setIsValid] = React.useState<boolean>(true);
  const textAreaAttr = type != "custom" ? enumInputTextType[type]['className']!.toString() : "";
  const textClassNames: string = makeTextAreaClassName();
  const [referenceElement, setReferenceElement] = useState<any>(null);
  const [popperElement, setPopperElement] = useState<any>(null);
  const [arrowElement, setArrowElement] = useState<any>(null);
  const { styles, attributes, forceUpdate } = usePopper(referenceElement, popperElement, {
    strategy: 'fixed',
    placement: props.popoverPlacement ?? 'auto',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement
        }
      },
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
    ],
  });

  function makeTextAreaClassName(): string {
    return `${className} ${textAreaAttr}`;
  }

  function generateResult(newValue: any, name: string, isValid: boolean) {
    let value = String(newValue ?? "");
    if (props.inputType === 'number') {
      value = value.replaceAll(',', '');
    }
    return {
      value: value,
      name: name,
      isValid: isValid
    } as ArcInputChangeResult;
  }

  const handleOnKeyup = (e: any) => {
    if (props.onKeyUp) {
      props.onKeyUp(e);
    }
  }

  useEffect(() => {
    if (!props.value) {
      setValueText(undefined);
    } else {
      setValueText(props.value);
    }
  }, [props.value]);

  const getOnChangeHandler = () => {
    switch (props.inputType) {
      case 'number':
        return handleNumberChange;
      case 'tel':
        return handleNumericChange;
      case 'text':
      default:
        return handleTextChange;
    }
  };

  const handleChange = (e: any) => {
    let value = e.target.value;
    let trimValue = e.target.value.trim();

    if (ObjectUtil.isEmpty(trimValue)) {
      value = trimValue;
    }

    let adjustValue = value;

    if (props.canChange) {
      const result: ArcRangeSliderCanChangeValueResult = props.canChange(value);

      if (!result.isValid) {
        return;
      }

      adjustValue = result.adjustValue ?? value;
    }

    setValueText(adjustValue);

    if (adjustValue) {
      if (props.pattern && !new RegExp(props.pattern).test(adjustValue)) {
        setIsValid(false);
        setValidateMsg("Invalid pattern");
      } else {
        const isValidInput = adjustValue.trim() !== "";
        setIsValid(isValidInput);
        setValidateMsg(""); // Clear the error message if the value is valid
      }
    } else {
      setIsValid(false);
      setValidateMsg(props.validateMessage);
    }
    props.onChange(generateResult(adjustValue, e.target.name, isValid));
  };

  const handleNumericChange = (e: any) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
    let adjustValue = value;

    if (props.canChange) {
      const result: ArcRangeSliderCanChangeValueResult = props.canChange(value);

      if (!result.isValid) {
        return;
      }

      adjustValue = result.adjustValue ?? value;
    }

    setValueText(adjustValue);

    const isValidInput = adjustValue.trim() !== "";
    setIsValid(isValidInput);
    setValidateMsg(isValidInput ? "" : props.validateMessage);

    props.onChange(generateResult(adjustValue, e.target.name, isValid));
  };

  const handleNumberChange = (e: any) => {
    const value = e.target.value.replace(/[^-\.0-9]/g, ''); // allowed only number, -, .

    // 소수점 자릿수 제한
    if (props.maxFraction !== undefined && value.includes('.')) {
      const decimalPart = value.split('.')[1];
      if (decimalPart && decimalPart.length > props.maxFraction) {
        return;
      }
    }

    if (props.maxFraction === 0 && value.includes('.')) {
      return;
    }

    let adjustValue = value;

    if (props.canChange) {
      const result: ArcRangeSliderCanChangeValueResult = props.canChange(value);

      if (!result.isValid) {
        return;
      }

      adjustValue = result.adjustValue ?? value;
    }

    setValueText(adjustValue);
    const isValidInput = adjustValue.trim() !== "";
    setIsValid(isValidInput);
    setValidateMsg(isValidInput ? "" : props.validateMessage);

    props.onChange(generateResult(adjustValue, e.target.name, isValid));
  };

  const handleTextChange = (e: any) => {
    handleChange(e);
  };

  const handleBlur = (e: any) => {
    let value: number = parseFloat(e.target.value?.replaceAll(',', ''));
    let adjustValue = value;

    if (props.canChange) {
      const result: ArcRangeSliderCanChangeValueResult = props.canChange(value);

      if (!result.isValid) {
        return;
      }

      adjustValue = result.adjustValue ?? value;
    }

    setValueText(adjustValue);

    if (adjustValue !== undefined && adjustValue !== null && adjustValue.toString() !== '') {
      if (props.minValue !== undefined && adjustValue < props.minValue) {
        adjustValue = props.minValue;
      } else if (props.maxValue !== undefined && adjustValue > props.maxValue) {
        adjustValue = props.maxValue;
      }
      setIsValid(true);
      setValueText(adjustValue);
      props.onChange(generateResult(adjustValue, e.target.name, isValid));
    } else {
      setValidateMsg(props.validateMessage);
      setIsValid(false);
    }
  };

  // prevent kor at number input
  function handleCompositionStart(e: any) {
    e.target.blur();
    requestAnimationFrame(function () {
      e.target.focus();
    })
  }

  function handleOnKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  }

  useEffect(() => {
    if (!isValid && props.isRequired && forceUpdate) {
      forceUpdate();
    }
  }, [isValid]);

  useEffect(() => {
    if (props.forceValidate && props.isRequired && !props.value) {
      setIsValid(false);
      setValidateMsg(props.validateMessage);
    }
  }, [props.forceValidate]);

  return (
    <>
      {displayTitle && props.label && (
        <label htmlFor={id} style={{marginRight: 0}}><span className="srh-tit">{props.label}</span></label>
      )}

      <div ref={setPopperElement} className="popper-tooltip" style={{
        ...styles.popper,
        display: `${!isValid && props.isRequired && props.validateMessage ? 'block' : 'none'}`
      }} {...attributes.popper}>
        {validateMsg}
        <div ref={setArrowElement} className="arrow" style={styles.arrow}/>
      </div>

      {props.inputType === 'number' ? (
        <input
          ref={setReferenceElement}
          id={id}
          type="number"
          name={ObjectUtil.isEmpty(name) ? id : name}
          value={valueText ?? ''}
          placeholder={placeholder?.toString()}
          style={props.style}
          min={props.minValue}
          max={props.maxValue}
          step={props.step ?? 1}
          className={`${isValid ? textClassNames : `${textClassNames} ${props.isRequired ? 'InpSel-wrong' : ''}`}`}
          onChange={getOnChangeHandler()}
          onKeyUp={handleOnKeyup}
          onKeyDown={handleOnKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readonly}
          pattern={props.pattern}
          maxLength={props.maxLength}
          title={props.title ?? ''}
        />
      ) : (
        <input
          ref={setReferenceElement}
          id={id}
          name={ObjectUtil.isEmpty(name) ? id : name}
          type={props.inputType}
          value={valueText ?? ""}
          placeholder={placeholder?.toString()}
          style={props.style}
          className={`${isValid ? textClassNames : `${textClassNames} ${props.isRequired ? 'InpSel-wrong' : ''}`}`}
          onChange={getOnChangeHandler()}
          onKeyUp={handleOnKeyup}
          onKeyDown={handleOnKeyDown}
          disabled={disabled}
          readOnly={readonly}
          pattern={props.pattern}
          maxLength={props.maxLength}
          title={props.title ?? ''}
        />
      )}

      {props.suffix && (
        <span className="input-util">{props.suffix}</span>
      )}

      {!isValid && props.isRequired && (
        <span className="sr-only">{validateMsg}</span>
      )}
    </>
  );
}